/*
 * The one announcer (A31, REUSE J1). Two live regions exist in the whole application and both are
 * here: a polite one for things that happened, and an assertive one reserved for failures. Two
 * regions racing produce interleaved speech, so nothing else in the tree may add one.
 *
 * Politeness is a rate, not a wish: at most one polite utterance every three seconds, and the last
 * one wins — a burst of arriving messages must not turn into a burst of speech ([25] §1). A reader
 * who has switched announcements off is not announced to at all, assertive failures included: the
 * banner and the toast still show them.
 *
 * **What a person just asked for is not held back.** The floor is about things that *arrive*; an
 * answer to a keystroke is the other kind, and holding it costs the reader the one fact they
 * pressed the key for. Somebody stepping through a list one key press at a time moves four times
 * in two seconds, and every stop after the first was swallowed — the caret moved and the region
 * kept the first sentence, which tells a reader who cannot see the page that nothing happened.
 *
 * **The same sentence twice is said twice.** A region speaks when its text *changes*, so setting
 * the sentence it already holds says nothing: the key pressed again at the last stop would be
 * silent the second time. Every utterance is therefore a new node in the region — the
 * sentence is keyed by a turn that counts up — so the region's content is replaced even when its
 * words are not, and the text a test or a reader finds is still exactly the sentence. Clearing the
 * region first does not do it: React renders the empty string and the sentence in one commit.
 */

import type { ReactNode } from "react"
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { LiveRegion } from "../../lib/liveRegion.tsx"
import { readValue, storageKey, writeValue } from "../../lib/storage.ts"
import { t, translate, type AnyKey, type Args, type Key } from "../i18n/index.ts"

/** The floor between two polite utterances. */
export const ANNOUNCE_INTERVAL_MS = 3000

export interface AnnounceOptions {
	/** A failure, spoken at once and through `role="alert"`. Never use it for a new message. */
	assertive?: boolean
	/**
	 * The answer to something the person has just done — a walk that moved, a filter that changed.
	 * It skips the politeness floor, because that floor exists to keep *arriving* things from
	 * becoming a burst of speech and a person pressing a key four times is owed four answers. Never
	 * use it for anything the reader did not ask for.
	 */
	answersAnAction?: boolean
}

/**
 * The one way to speak to a reader. A key of this build is typed down to its placeholders; a
 * module's own key — which arrives with its route bundle and is therefore not part of `Key` — is
 * accepted with a plain argument record, exactly as `translate()` renders it. Without that a module
 * would have to keep a live region of its own, which is the thing this file exists to prevent.
 */
type Announce = <K extends AnyKey>(
	key: K,
	args?: K extends Key ? Args<K> : Record<string, string | number>,
	options?: AnnounceOptions,
) => void

const AnnouncerContext = createContext<Announce>(() => undefined)

const MUTED_KEY = storageKey("announcerMuted")

/** One thing said: its words, and which utterance it is, so saying the same words again is new. */
interface Utterance {
	text: string
	turn: number
}

const SILENT: Utterance = { text: "", turn: 0 }

/** True while this device has announcements switched off (the appearance setting). */
export function announcementsMuted(): boolean {
	return readValue(MUTED_KEY) === "1"
}

export function setAnnouncementsMuted(muted: boolean): void {
	writeValue(MUTED_KEY, muted ? "1" : "")
}

export function AnnouncerProvider({ children }: { children: ReactNode }) {
	const [polite, setPolite] = useState(SILENT)
	const [assertive, setAssertive] = useState(SILENT)
	const lastAt = useRef(0)
	const turns = useRef(0)
	/** The sentence as it goes into a region: a new utterance each time, even of the same words. */
	const fresh = useCallback((text: string): Utterance => {
		turns.current += 1
		return { text, turn: turns.current }
	}, [])
	const pending = useRef<{ text: string; timer: ReturnType<typeof setTimeout> } | null>(null)

	useEffect(
		() => () => {
			if (pending.current) clearTimeout(pending.current.timer)
		},
		[],
	)

	const announce = useCallback<Announce>(
		(key, args, options) => {
			if (announcementsMuted()) return
			const text = translate(key, args)
			if (options?.assertive === true) {
				// A failure is not coalesced: it is the one thing a reader must hear immediately, and two
				// identical failures speak twice.
				setAssertive(fresh(text))
				return
			}
			const now = Date.now()
			const wait =
				options?.answersAnAction === true ? 0 : ANNOUNCE_INTERVAL_MS - (now - lastAt.current)
			if (wait <= 0) {
				// An answer that overtakes a held utterance takes its place: the reader asked for this
				// one, and what was waiting is by then about something they have moved on from.
				if (pending.current) clearTimeout(pending.current.timer)
				pending.current = null
				lastAt.current = now
				setPolite(fresh(text))
				return
			}
			if (pending.current) clearTimeout(pending.current.timer)
			pending.current = {
				text,
				timer: setTimeout(() => {
					const held = pending.current
					pending.current = null
					if (!held) return
					lastAt.current = Date.now()
					setPolite(fresh(held.text))
				}, wait),
			}
		},
		[fresh],
	)

	return (
		<AnnouncerContext.Provider value={announce}>
			{children}
			<LiveRegion label={t("shell.announcer.polite")}>
				<span key={polite.turn}>{polite.text}</span>
			</LiveRegion>
			<LiveRegion mode="assertive" label={t("shell.announcer.alerts")}>
				<span key={assertive.turn}>{assertive.text}</span>
			</LiveRegion>
		</AnnouncerContext.Provider>
	)
}

/** `announce("shell.quickSwitcher.results", { count })` — the only way to speak to a reader. */
export function useAnnounce(): Announce {
	return useContext(AnnouncerContext)
}
